import { CustomQuestionsForm, questionErrors, useCustomQuestions } from './CustomQuestions';
import { isBuiltinQuestion, type CustomAnswer } from '@lub0v/santa-utils';
import { ParticipantPicturePicker } from '@src/components/PicturePicker/ParticipantPicturePicker';
import { useWishHint } from './useWishHint';
import './EditCardPage.css';
import { CardDetailHeader } from '@src/components/CardDetail/CardDetail';
import {
  normalizeWishLinks,
  normalizeCompletedWishLinks,
} from '@src/components/WishText/formattedLinks';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, FullCardInfo } from '@lub0v/santa-utils';
import {
  Button,
  Divider,
  HintError,
  Input,
  SettingsPage,
  SettingsPanel,
  SettingsSection,
  Typography,
  WarningIcon,
  SettingsActionRow,
  Stack,
  Textarea,
  showDialog,
  showToast,
} from '@src/components';

import { boxPath, boxCardPath } from '@src/routing/routes';
import { santaClient } from '@src/state/client';
import { useAppDispatch } from '@src/state/hooks';
import { loadSession } from '@src/state/sessionSlice';

export interface EditCardPageProps {
  info: BoxInfo;
  card: FullCardInfo;
  onChanged: () => Promise<void> | void;
}

export function EditCardPage({ info, card, onChanged }: EditCardPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const wishHint = useWishHint(info.box, false);
  const dispatch = useAppDispatch();
  const questions = useCustomQuestions(info.box.key);
  const typedQuestionnaire = questions.fields.some(isBuiltinQuestion);
  const legacyQuestionnaire = !questions.loading && !questions.error && !typedQuestionnaire;
  const [answers, setAnswers] = useState<CustomAnswer[]>(() =>
    (card.customAnswers ?? []).map(({ fieldId, value }) => ({ fieldId, value })),
  );
  const [answerErrors, setAnswerErrors] = useState<Record<string, string>>({});
  const answerSaveQueue = useRef<Promise<void>>(Promise.resolve());
  async function saveCustomAnswers() {
    const errors = questionErrors(questions.fields, answers, card.customAnswers ?? []);
    setAnswerErrors(errors);
    if (Object.keys(errors).length) return;
    // Serialize snapshots without disabling the next field while it receives focus.
    // Disabling the whole form on blur can discard the next keystroke.
    const previous = answerSaveQueue.current;
    let release!: () => void;
    answerSaveQueue.current = new Promise<void>((resolve) => { release = resolve; });
    try {
      await previous;
      await saveQueue.current;
      const res = await santaClient.saveCard({
        key: info.box.key,
        username: saved.current.username,
        customAnswers: answers,
      });
      if (!res.ok) setError(t(res.error?.message || 'newBox.saveFailed'));
      else {
        setError(null);
        showToast(t('game.settings.saved'), 'success');
        await onChanged();
      }
    } catch {
      setError(t('newBox.saveFailed'));
    } finally {
      release();
    }
  }
  const archived = Boolean(info.box.isArchived);
  const [username, setUsername] = useState(card.username || '');
  const [picture, setPicture] = useState(card.picture || 'fox');
  const [avatar, setAvatar] = useState(card.avatar || '');
  const [wish, setWish] = useState(() =>
    normalizeWishLinks(card.wish || '', t('game.card.wishLinkLabel')),
  );
  const [postalAddress, setPostalAddress] = useState(card.postalAddress || '');
  const [postalCode, setPostalCode] = useState(card.postalCode || '');
  const [postalRecipient, setPostalRecipient] = useState(card.postalRecipient || '');
  const [phone, setPhone] = useState(card.phone || '');
  const [error, setError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const leavingRef = useRef(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const saved = useRef({
    key: info.box.key,
    username: card.username || '',
    picture: card.picture || 'fox',
    avatar: card.avatar || '',
    wish: card.wish || '',
    postalAddress: card.postalAddress || '',
    postalCode: card.postalCode || '',
    postalRecipient: card.postalRecipient || '',
    phone: card.phone || '',
  });
  type CardPatch = Partial<Omit<typeof saved.current, 'key'>>;
  const failed = useRef<CardPatch>({});
  const saveQueue = useRef(Promise.resolve());

  function save(patch: CardPatch) {
    if (archived || leavingRef.current) return;
    saveQueue.current = saveQueue.current.then(async () => {
      const changes = { ...failed.current, ...patch };
      if (
        Object.entries(changes).every(
          ([key, value]) => saved.current[key as keyof CardPatch] === value,
        )
      )
        return;
      const next = { ...saved.current, ...changes };
      try {
        const res = await santaClient.saveCard(next);
        if (!res.ok) {
          failed.current = changes;
          setError(t(res.error?.message || 'newBox.saveFailed'));
          return;
        }
        saved.current = next;
        failed.current = {};
        setError(null);
        showToast(t('game.settings.saved'), 'success');
      } catch {
        failed.current = changes;
        setError(t('newBox.saveFailed'));
        return;
      }
      try {
        await onChanged();
      } catch {
        showToast(t('newBox.saveFailed'));
      }
    });
  }

  function saveName() {
    const value = username.trim();
    if (value.length < 2) {
      setNameError(t('game.card.nameTooShort'));
      return;
    }
    setNameError(null);
    setUsername(value);
    save({ username: value });
  }

  function confirmDelete() {
    showDialog({
      title: t('game.card.deleteTitle'),
      children: t('game.card.deleteBody'),
      cancel: t('box.cancel'),
      submit: t('game.card.delete'),
      submitVariant: 'danger',
      onSubmit: async () => {
        leavingRef.current = true;
        setLeaving(true);
        try {
          await saveQueue.current;
          const res = await santaClient.deleteCard({ key: info.box.key });
          if (!res.ok) {
            showToast(t(res.error?.message || 'newBox.saveFailed'));
            throw new Error('delete failed');
          }
          await dispatch(loadSession());
          navigate(boxPath(info.box.key));
        } finally {
          leavingRef.current = false;
          setLeaving(false);
        }
      },
    });
  }

  return (
    <SettingsPage
      embedded
      width="full"
      layout="panels"
      className="edit-card-page"
      testId="game-edit-card"
    >
      <Stack gap={2}>
        <CardDetailHeader
          title={t('game.card.editorTitle')}
          backLabel={t('game.exchange.myCard')}
          onBack={() => navigate(boxCardPath(info.box.key))}
          testId="game-card-back"
        />
      </Stack>
      {error ? (
        <Stack gap={2}>
          <HintError error={error} />
          <Button variant="secondary" onClick={() => save({})}>
            {t('game.card.retrySave')}
          </Button>
        </Stack>
      ) : null}
      <SettingsPanel edges={{ pieces: 3 }}>
        <Stack gap={6} className="edit-card-page__panel-content" testId="edit-card-questionnaire">
          <Stack gap={4}>
            <Typography as="h3" role="title/card">
              {t('game.card.questionnaireTitle')}
            </Typography>
            <Typography as="p" role="meta/helper">
              {t('game.card.questionnaireSaveHint')}
            </Typography>
          </Stack>
          <fieldset className="custom-question" disabled={archived || leaving}>
            <legend>{t('game.cardCreate.name.label')}</legend>
            <Typography as="p" role="meta/helper">
              {t('game.cardCreate.name.hint')}
            </Typography>
            <Input
              value={username}
              onValueChange={setUsername}
              onBlur={saveName}
              error={nameError || undefined}
              aria-label={t('game.cardCreate.name.label')}
              className="card-questionnaire__name"
              maxLength={64}
              disabled={archived || leaving}
              testId="edit-card-name"
            />
          </fieldset>
          {questions.loading && <p role="status">{t('questions.loading')}</p>}
          {questions.error && (
            <>
              <HintError error={questions.error} />
              <Button onClick={questions.retry}>{t('questions.retry')}</Button>
            </>
          )}
          {questions.fields.length > 0 && <Divider />}
          <CustomQuestionsForm
            showHeading={false}
            wishHint={wishHint}
            testIdPrefix="edit-card"
            onFieldBlur={() => void saveCustomAnswers()}
            fields={questions.fields}
            answers={answers}
            onChange={(nextAnswers) => {
              setAnswers(nextAnswers);
              setAnswerErrors({});
            }}
            errors={answerErrors}
            disabled={archived}
          />
          {legacyQuestionnaire && info.box.useWish && (
            <>
              <Divider />
              <fieldset className="custom-question" disabled={archived || leaving}>
                <legend>{t('game.cardCreate.wish.header')}</legend>
                <Typography as="p" role="meta/helper">
                  {wishHint}
                </Typography>
                <Textarea
                  value={wish}
                  onValueChange={(value) =>
                    setWish(normalizeCompletedWishLinks(value, t('game.card.wishLinkLabel')))
                  }
                  onBlur={() => {
                    const value = normalizeWishLinks(wish, t('game.card.wishLinkLabel'));
                    setWish(value);
                    save({ wish: value });
                  }}
                  aria-label={t('game.cardCreate.wish.label')}
                  maxLength={32768}
                  rows={5}
                  disabled={archived || leaving}
                  testId="edit-card-wish"
                />
              </fieldset>
            </>
          )}
          {legacyQuestionnaire && info.box.usePost && (
            <>
              <Divider />
              <fieldset className="custom-question" disabled={archived || leaving}>
                <legend>{t('game.cardCreate.delivery.sectionTitle')}</legend>
                <Typography as="p" role="meta/helper">
                  {t('questions.visibility.santa')}
                </Typography>
                <Input
                  name="postalRecipient"
                  maxLength={256}
                  autoComplete="shipping name"
                  value={postalRecipient}
                  onValueChange={setPostalRecipient}
                  onBlur={() => save({ postalRecipient })}
                  label={t('game.cardCreate.delivery.recipient')}
                  disabled={archived || leaving}
                />
                <div className="field-address-row">
                  <Input
                    name="postalAddress"
                    maxLength={256}
                    autoComplete="shipping street-address"
                    value={postalAddress}
                    onValueChange={setPostalAddress}
                    onBlur={() => save({ postalAddress })}
                    label={t('game.cardCreate.delivery.address')}
                    disabled={archived || leaving}
                  />
                  <Input
                    name="postalCode"
                    autoComplete="shipping postal-code"
                    value={postalCode}
                    onValueChange={setPostalCode}
                    onBlur={() => save({ postalCode })}
                    label={t('game.cardCreate.delivery.code')}
                    maxLength={12}
                    disabled={archived || leaving}
                  />
                </div>
              </fieldset>
            </>
          )}
          {legacyQuestionnaire && (info.box.usePost || info.box.isPhoneRequired) && (
            <fieldset className="custom-question" disabled={archived || leaving}>
              <legend>{t('game.cardCreate.delivery.phone')}</legend>
              <Typography as="p" role="meta/helper">
                {t('questions.visibility.santa')}
              </Typography>
              <Input
                name="phone"
                autoComplete="shipping tel"
                type="tel"
                value={phone}
                onValueChange={setPhone}
                onBlur={() => save({ phone })}
                aria-label={t('game.cardCreate.delivery.phone')}
                maxLength={32}
                disabled={archived || leaving}
              />
            </fieldset>
          )}
        </Stack>
      </SettingsPanel>
      <SettingsPanel edges={{ pieces: 4 }}>
        <Stack gap={4} className="edit-card-page__panel-content" testId="edit-card-picture-panel">
          <Typography as="h3" role="title/card">
            {t('game.cardCreate.picture.header')}
          </Typography>
          <Typography as="p" role="meta/helper">
            {t('game.card.avatarSaveHint')}
          </Typography>
          <ParticipantPicturePicker
            value={picture}
            avatar={avatar}
            disabled={archived || leaving}
            onValueChange={(value, photo) => {
              setPicture(value);
              setAvatar(photo);
              save({ picture: value, avatar: photo });
            }}
            testId="edit-card-picture"
          />
        </Stack>
      </SettingsPanel>
      {archived ? null : (
        <SettingsPanel edges={{ pieces: 4 }}>
          <SettingsSection title={t('game.settings.danger')} icon={<WarningIcon />}>
            <SettingsActionRow
              title={t('game.card.delete')}
              hint={t('game.card.deleteHint')}
              danger
            >
              <Button
                variant="danger"
                size="small"
                onClick={confirmDelete}
                disabled={leaving}
                testId="edit-card-delete"
              >
                {t('game.card.delete')}
              </Button>
            </SettingsActionRow>
          </SettingsSection>
        </SettingsPanel>
      )}
    </SettingsPage>
  );
}
